import { useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import { ArrowLeft, Search } from "lucide-react"
import { usePreventScroll } from "@/hooks/usePreventScroll"
import useProductStore from "@/store/productStore"
import { toast } from "@/hooks/use-toast"
import Loader from "./loader"
import InfiniteScroll from "./InfiniteScroll"
import { ProductList } from "../products/ProductList"
import Image from "next/image"

interface MobileSearchBarProps {
  showSearch: boolean
  onClose: () => void
}

export default function MobileSearchBar({ showSearch, onClose }: MobileSearchBarProps) {
  	const [loading, setLoading] = useState(false)
    const [search,setSearch]=useState("")
    const [debouncedSearch, setDebouncedSearch] = useState(search)

	const { fetchProducts, productList,productMetadata,loadMoreProducts } = useProductStore()
  useEffect(() => {
    loadProducts()
  }, [debouncedSearch])
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const loadProducts = async () => {
    setLoading(true)
    const result = await fetchProducts({ filter: {}, sort: {}, search: debouncedSearch })
    setLoading(false)
    if (!result.status) {
      toast({
        variant: "destructive",
        title: "Error",
        description: result.error,
      })
    }
	}
  if(!showSearch) return null
  return (
    <>
    {/* Backdrop */}
    <div 
      className={`fixed inset-0 z-[99] transition-all duration-300 ease-out ${
        showSearch ? 'bg-black/40 backdrop-blur-sm opacity-100' : 'bg-black/0 opacity-0 pointer-events-none'
      } md:flex md:items-center md:justify-center`}
      onClick={onClose}
    >
      {/* Modal */}
      <div 
        className={`bg-white flex flex-col transition-all duration-300 ease-out ${
          showSearch 
            ? 'translate-y-0 opacity-100 scale-100' 
            : 'translate-y-4 opacity-0 scale-95'
        } md:max-w-3xl md:w-full md:h-[600px] md:rounded-2xl md:shadow-2xl md:m-4 h-full`}
        onClick={(e) => e.stopPropagation()}
      >
      <div className="flex items-center p-4 border-b">
        <button
          className="mr-2"
          aria-label="Back"
          onClick={onClose}
        >
          <Image src={"/back-arrow.svg"} alt="back" width={18} height={18} />
        </button>
        <input
          autoFocus
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder='Search for products'
          className="flex-1 rounded-full bg-[#F7F7FA] px-4 py-2 text-sm focus:outline-none"
        />
        <Search className="ml-2 w-5 h-5 text-gray-400" />
      </div>
      <div className="p-4 h-full overflow-y-auto">
        <div className="font-semibold mb-2">Search results</div>
        <div className="flex-1">
          {
            loading ? (
              <Loader />
            ) : (
              productList?.length > 0 ? (
                <InfiniteScroll loading={loading} hasMore={productMetadata?.totalPages > productMetadata?.page} loadMore={() => loadMoreProducts({ filter: {}, page: productMetadata?.page + 1, limit: productMetadata?.limit, search: debouncedSearch })}>
                  <ProductList products={productList} />
                </InfiniteScroll>
              ) : (
                <div className="flex flex-col min-h-[50vh] justify-center items-center">
                  <p className="text-gray-400  text-base">No products found</p>
                  {search && <p className="text-xs text-gray-300 mt-2">Please modify the search</p>}
                </div>
              )
            )
          }
        </div>
      </div>
    </div>
    </div>
  </>
  )
}